/**
 * Products API Client
 * Handles product CRUD operations and variant management
 */

import { apiClient, API_PREFIX } from './client';

export interface ProductImageSizes {
  thumbnail?: string;
  medium?: string;
  large?: string;
}

export type ProductImage = string | ProductImageSizes;

export interface Product {
  id: string;
  merchantId: string;
  categoryId?: string;
  name: string;
  nameTh?: string;
  description?: string;
  descriptionTh?: string;
  price: number;
  salePrice?: number | null;
  isOnSale: boolean;
  soldCount?: number;
  weight?: number;
  tags?: string[];
  dimensions?: {
    length?: number;
    width?: number;
    height?: number;
  };
  images: ProductImage[];
  warehouse: 'HYB' | 'PP';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  categoryName?: string;
  categoryNameTh?: string;
  variants?: ProductVariant[];
}

export interface VariantData {
  imageIndex?: number;  // Index of image in product.images[] to scroll to when selected
  label?: string;
  [key: string]: any;
}

export interface ProductVariant {
  id: string;
  productId: string;
  name: string;
  sku?: string;
  priceAdjustment: number;
  isAvailable: boolean;
  variantData?: VariantData;
  createdAt: string;
  updatedAt: string;
}

export interface ProductCreateData {
  merchantId: string;
  categoryId?: string;
  name: string;
  description?: string;
  price: number;
  salePrice?: number | null;
  isOnSale?: boolean;
  soldCount?: number;
  tags?: string[];
  warehouse: 'HYB' | 'PP';
  images: string[]; // Base64 encoded images
}

export interface ProductUpdateData {
  categoryId?: string;
  name?: string;
  description?: string;
  price?: number;
  salePrice?: number | null;
  isOnSale?: boolean;
  soldCount?: number;
  tags?: string[];
  warehouse?: 'HYB' | 'PP';
  isActive?: boolean;
  images?: string[]; // Base64 encoded images
}

export interface VariantCreateData {
  productId: string;
  name: string;
  sku?: string;
  priceAdjustment: number;
  variantData?: VariantData;
  sortOrder?: number;
}

export interface VariantUpdateData {
  name?: string;
  sku?: string;
  priceAdjustment?: number;
  variantData?: VariantData;
  isAvailable?: boolean;
  sortOrder?: number;
}

export interface ProductListResponse {
  success: boolean;
  products: Product[];
  pagination: {
    page: number;
    perPage: number;
    total: number;
    pages: number;
  };
  statistics: {
    totalOnSale: number;
  };
}

export interface ProductDetailResponse {
  success: boolean;
  product: Product;
}

export interface VariantResponse {
  success: boolean;
  variant: ProductVariant;
}

export const productsApi = {
  /**
   * Create a new product
   */
  createProduct: async (data: ProductCreateData): Promise<Product> => {
    const response = await apiClient.post<{ success: boolean; data: Product }>(
      `${API_PREFIX}/products`,
      data
    );
    return response.data.data;
  },

  /**
   * Get all categories
   */
  listCategories: async (): Promise<Array<{
    id: string;
    name: string;
    nameTh: string;
    description?: string;
    sortOrder: number;
    isActive: boolean;
  }>> => {
    const response = await apiClient.get<{ success: boolean; data: any[] }>(
      `${API_PREFIX}/products/categories`
    );
    return response.data.data;
  },

  /**
   * Get all products with pagination and filters
   */
  getProducts: async (params?: {
    categoryId?: string;
    search?: string;
    page?: number;
    perPage?: number;
    includeInactive?: boolean;
    includeVariants?: boolean;
    warehouse?: 'HYB' | 'PP';
  }): Promise<ProductListResponse> => {
    const searchParams = new URLSearchParams();
    if (params?.categoryId) searchParams.append('categoryId', params.categoryId);
    if (params?.search) searchParams.append('search', params.search);
    if (params?.page) searchParams.append('page', params.page.toString());
    if (params?.perPage) searchParams.append('perPage', params.perPage.toString());
    if (params?.includeInactive !== undefined) searchParams.append('includeInactive', params.includeInactive.toString());
    if (params?.includeVariants !== undefined) searchParams.append('includeVariants', params.includeVariants.toString());
    if (params?.warehouse) searchParams.append('warehouse', params.warehouse);

    const response = await apiClient.get<{ success: boolean; data: ProductListResponse }>(
      `${API_PREFIX}/products?${searchParams.toString()}`
    );
    return response.data.data;
  },

  /**
   * Get a single product by ID
   */
  getProduct: async (
    productId: string,
    includeVariants: boolean = true
  ): Promise<Product> => {
    const params = includeVariants ? '?includeVariants=true' : '';
    const response = await apiClient.get<ProductDetailResponse>(
      `${API_PREFIX}/products/${productId}${params}`
    );
    return response.data.product;
  },

  /**
   * Update a product
   */
  updateProduct: async (
    productId: string,
    data: ProductUpdateData
  ): Promise<Product> => {
    const response = await apiClient.patch<{ success: boolean; data: Product }>(
      `${API_PREFIX}/products/${productId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete a product (permanently delete from database)
   */
  deleteProduct: async (productId: string): Promise<void> => {
    await apiClient.delete(`${API_PREFIX}/products/${productId}`);
  },

  /**
   * Create a product variant
   */
  createVariant: async (data: VariantCreateData): Promise<ProductVariant> => {
    const response = await apiClient.post<{ success: boolean; data: ProductVariant }>(
      `${API_PREFIX}/products/variants`,
      data
    );
    return response.data.data;
  },

  /**
   * Update a product variant
   */
  updateVariant: async (variantId: string, data: VariantUpdateData): Promise<ProductVariant> => {
    const response = await apiClient.put<{ success: boolean; data: ProductVariant }>(
      `${API_PREFIX}/products/variants/${variantId}`,
      data
    );
    return response.data.data;
  },

  /**
   * Delete a product variant (soft delete)
   */
  deleteVariant: async (variantId: string): Promise<void> => {
    await apiClient.delete(`${API_PREFIX}/products/variants/${variantId}`);
  },

  /**
   * Search products
   */
  searchProducts: async (
    searchTerm: string,
    page: number = 1,
    perPage: number = 20
  ): Promise<ProductListResponse> => {
    return productsApi.getProducts({
      search: searchTerm,
      page,
      perPage,
    });
  },
};
